/**
 * components/landing/home/architecture/ViewModeSwitcher.jsx — the tab bar
 * that lets the visitor toggle between the 5 architecture perspectives
 * defined in `data.js` (Overview, AI, Trading, Ops, White-Label).
 *
 * Purely presentational — the parent owns the active state and passes
 * `active` + `onChange` down.  Every mode is keyboard-reachable via the
 * arrow keys and Tab.  A subtle highlight bar animates under the active
 * pill using an inline transform + transition (no framer-motion).
 */

import React from 'react'
import clsx from 'clsx'
import { Layers, Sparkles, LineChart, Building2, Briefcase } from 'lucide-react'
import { VIEW_MODES, TONE_COLORS } from './data'

const ICONS = {
  overview: Layers,
  ai:       Sparkles,
  trading:  LineChart,
  ops:      Briefcase,
  wl:       Building2,
}

export default function ViewModeSwitcher({ activeId, onChange, className = '' }) {
  return (
    <div className={clsx('flex flex-wrap items-center justify-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-xl p-1.5', className)}
      role="tablist" aria-label="Architecture view mode"
    >
      {VIEW_MODES.map((m) => {
        const t = TONE_COLORS[m.tone]
        const active = m.id === activeId
        const Icon = ICONS[m.id] || Layers
        return (
          <button
            key={m.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(m.id)}
            className={clsx(
              'inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60',
              active
                ? 'text-black shadow-[0_10px_30px_-10px_rgba(255,193,7,0.55)]'
                : 'text-zinc-300 hover:text-white hover:bg-white/[0.04]',
            )}
            style={active ? { background: `linear-gradient(90deg, ${t.hex}, #FFE17A)` } : undefined}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{m.label}</span>
            <span className="sm:hidden">{m.short}</span>
          </button>
        )
      })}
    </div>
  )
}
